import type { TFunction } from 'i18next';
import { COUNTRY_CODES, type CountryCode } from '../data/countries';

// Longest dial codes first so "+216" wins over "+2" when matching by prefix.
const COUNTRY_CODES_BY_PREFIX = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);

const findCountryForPhone = (phone: string): CountryCode | undefined =>
  COUNTRY_CODES_BY_PREFIX.find((country) => phone.startsWith(country.code));

/**
 * Format a phone number for display by splitting it into "+<code> <local>".
 * Works for every country in COUNTRY_CODES — falls back to the raw value
 * when the dial code is unknown.
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  const normalized = phone.replace(/\s/g, '');
  const country = findCountryForPhone(normalized);
  if (!country) return phone;
  return `${country.code} ${normalized.slice(country.code.length)}`.trim();
};

/**
 * Validate a phone number against any country in COUNTRY_CODES. The local
 * portion must be 4..15 digits (E.164 max minus the dial-code length).
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  if (!phone) return false;
  const normalized = phone.replace(/\s/g, '');
  const country = findCountryForPhone(normalized);
  if (!country) return false;
  const local = normalized.slice(country.code.length);
  if (!/^\d+$/.test(local)) return false;
  const maxLocal = Math.max(4, 15 - country.code.replace(/\D/g, '').length);
  return local.length >= 4 && local.length <= maxLocal;
};

/**
 * Format currency amount
 * @param amount - Amount to format
 * @param currency - Currency code (default: TND)
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, currency: string = 'TND'): string => {
  return new Intl.NumberFormat('en-TN', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

/**
 * Format date to readable string
 * @param date - Date to format
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | Date,
  options?: Intl.DateTimeFormatOptions
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  }).format(dateObj);
};

/**
 * Get initials from name
 * @param name - Full name
 * @returns Initials (max 2 characters)
 */
export const getInitials = (name: string): string => {
  if (!name) return '';
  
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Get status color based on agency status
 * @param status - Agency status
 * @returns Color code
 */
export const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    pending_verification: '#F59E0B',
    profile_incomplete: '#D1D5DB',
    under_review: '#3B82F6',
    approved: '#22C55E',
    suspended: '#EF4444',
    rejected: '#EF4444',
  };
  
  return statusColors[status] || '#9CA3AF';
};

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * Calculate profile completion percentage
 * @param requirements - Requirements object from agency
 * @returns Completion percentage (0-100)
 */
export const calculateProfileCompletion = (
  requirements?: Record<string, boolean>
): number => {
  if (!requirements) return 0;
  
  const values = Object.values(requirements);
  if (values.length === 0) return 0;
  
  const completed = values.filter(Boolean).length;
  return Math.round((completed / values.length) * 100);
};

/**
 * Debounce function
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Parse API error message
 * @param error - Error object
 * @returns User-friendly error message
 */
export const parseApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
    return error.response.data.errors.map((e: any) => e.message).join(', ');
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Sleep/delay function
 * @param ms - Milliseconds to sleep
 * @returns Promise that resolves after delay
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Get full URL for uploaded images (profiles, vehicles, etc.)
 * Converts backend file paths to proper API URLs
 * @param imagePath - Relative path from backend (e.g., "uploads/profiles/...")
 * @returns Full URL to access the image
 */
export const getImageUrl = (imagePath: string | undefined | null): string | null => {
  if (!imagePath) return null;

  if (imagePath.startsWith('data:image/')) {
    return imagePath;
  }
  
  const isLocalhost = window.location.hostname === 'localhost';
  const defaultBaseUrl = isLocalhost
    ? 'http://localhost:5000'
    : `${window.location.protocol}//api.${window.location.hostname.replace(/^www\.|^app\./, '')}`;
  const baseUrl = import.meta.env.VITE_API_URL || defaultBaseUrl;

  // If already a full URL (http/https), return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    const isProduction = !isLocalhost;
    if (isProduction && imagePath.includes('localhost:5000')) {
      return imagePath.replace(/http:\/\/localhost:5000/g, baseUrl);
    }
    // If we are on localhost but the URL is preprod, and the image might be local
    if (isLocalhost && imagePath.includes('preprod.api.cararabia.app')) {
      return imagePath.replace(/https:\/\/preprod\.api\.cararabia\.app/g, baseUrl);
    }
    return imagePath;
  }
  
  // Handle relative paths from the backend
  let cleanPath = imagePath;

  // If it's an absolute path from backend (Windows or Linux origin)
  if (cleanPath.match(/^[A-Za-z]:[\\/]/) || cleanPath.includes('/uploads/')) {
    // Extract everything from 'uploads' onwards
    const match = cleanPath.match(/uploads[\\/].*$/);
    if (match) {
      cleanPath = match[0].replace(/\\/g, '/'); // Normalize slashes
    }
  }

  // Remove leading slash
  if (cleanPath.startsWith('/')) {
    cleanPath = cleanPath.slice(1);
  }

  if (cleanPath.startsWith('api/')) {
    return `${baseUrl}/${cleanPath}`;
  }

  // If it's a profile or vehicle path but missing 'uploads/', prepend it
  // This handles paths like 'profiles/...' or 'vehicles/...' saved in DB
  if (
    (cleanPath.startsWith('profiles/') || cleanPath.startsWith('vehicles/')) && 
    !cleanPath.startsWith('uploads/')
  ) {
    cleanPath = `uploads/${cleanPath}`;
  }

  // If it's a static asset in public/images, don't prepend baseUrl (let Vite handle it)
  if (cleanPath.startsWith('images/')) {
    return `/${cleanPath}`;
  }
  
  // Construct full API URL
  return `${baseUrl}/${cleanPath}`;
};

/**
 * Interface for overdue calculation results
 */
export interface OverdueInfo {
  days: number;
  hours: number;
  totalHours: number;
  isOverdue: boolean;
}

/**
 * Calculate overdue days and hours from an end date
 * @param endDate - The end date string
 * @returns OverdueInfo object with days, hours, and total hours
 */
export const calculateOverdue = (endDate: string | undefined | null): OverdueInfo => {
  if (!endDate) {
    return { days: 0, hours: 0, totalHours: 0, isOverdue: false };
  }
  
  try {
    const end = new Date(endDate);
    const now = new Date();
    const diffMs = now.getTime() - end.getTime();
    
    if (diffMs <= 0) {
      return { days: 0, hours: 0, totalHours: 0, isOverdue: false };
    }
    
    const diffHours = diffMs / (1000 * 60 * 60);
    const days = Math.floor(diffHours / 24);
    const hours = Math.floor(diffHours % 24);
    const totalHours = Math.floor(diffHours);
    
    return {
      days,
      hours,
      totalHours,
      isOverdue: true
    };
  } catch (error) {
    console.error('Error calculating overdue:', error);
    return { days: 0, hours: 0, totalHours: 0, isOverdue: false };
  }
};

/**
 * Format overdue information for display
 * @param overdueInfo - OverdueInfo object
 * @param t - optional i18next translate function; when provided the units are localized
 * @returns Formatted string for display
 */
export const formatOverdueDisplay = (
  overdueInfo: OverdueInfo,
  t?: TFunction,
): string => {
  if (!overdueInfo.isOverdue) {
    return '';
  }

  const parts: string[] = [];

  if (overdueInfo.days > 0) {
    const unit = overdueInfo.days === 1 ? 'day' : 'days';
    parts.push(
      t
        ? t('bookings.timeWithCount', {
            count: overdueInfo.days,
            unit: t(`bookings.timeUnits.${unit}`, { defaultValue: unit }),
            defaultValue: `${overdueInfo.days} ${unit}`,
          })
        : `${overdueInfo.days} ${unit}`,
    );
  }

  if (overdueInfo.hours > 0 || overdueInfo.days === 0) {
    const unit = overdueInfo.hours === 1 ? 'hour' : 'hours';
    parts.push(
      t
        ? t('bookings.timeWithCount', {
            count: overdueInfo.hours,
            unit: t(`bookings.timeUnits.${unit}`, { defaultValue: unit }),
            defaultValue: `${overdueInfo.hours} ${unit}`,
          })
        : `${overdueInfo.hours} ${unit}`,
    );
  }

  return parts.join(' ');
};
